import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Link2, ExternalLink, X, QrCode, Camera, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const QR_ELEMENT_ID = "vezice-qr-reader";

/**
 * Floating "Vezice" button.
 *   - "url" mode  → iframe of LZS / custom URL.
 *   - "qr"  mode  → opens phone camera, scans QR (e.g. ZGS vezica) → opens result URL.
 */
const VeziceFAB = () => {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [scannedUrl, setScannedUrl] = useState(null);
  const [scannerStatus, setScannerStatus] = useState("idle"); // idle | starting | running | error
  const [scannerError, setScannerError] = useState("");
  const scannerInstanceRef = useRef(null);
  const startAttemptRef = useRef(0);

  useEffect(() => {
    if (!token || !user) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/vezice`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setSettings(res.data);
      } catch (_) {
        if (alive) setSettings(null);
      }
    })();
    return () => { alive = false; };
  }, [token, user]);

  const label = "Vezice";

  const isQrMode = settings?.mode === "qr" && !scannedUrl;
  const effectiveUrl = scannedUrl || settings?.url;

  const stopScanner = async () => {
    const inst = scannerInstanceRef.current;
    scannerInstanceRef.current = null;
    if (!inst) return;
    try {
      await inst.stop();
      try { inst.clear(); } catch (_) { /* ignore */ }
    } catch (_) { /* ignore */ }
  };

  const startScanner = async () => {
    const attempt = ++startAttemptRef.current;
    setScannerStatus("starting");
    setScannerError("");
    // Wait for the target element to be present in the DOM
    let el = null;
    for (let i = 0; i < 20; i++) {
      el = document.getElementById(QR_ELEMENT_ID);
      if (el) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    if (attempt !== startAttemptRef.current) return;
    if (!el) {
      setScannerStatus("error");
      setScannerError("Element skenerja ni pripravljen.");
      return;
    }
    // Ensure clean state
    await stopScanner();
    try {
      const mod = await import("html5-qrcode");
      const Html5Qrcode = mod.Html5Qrcode;
      const instance = new Html5Qrcode(QR_ELEMENT_ID, { verbose: false });
      scannerInstanceRef.current = instance;
      await instance.start(
        { facingMode: { exact: "environment" } },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        (decodedText) => {
          try { instance.stop().catch(() => { /* ignore */ }); } catch (_) { /* ignore */ }
          const raw = (decodedText || "").trim();
          if (!raw) return;
          if (!/^https?:\/\//i.test(raw)) {
            toast.success("QR koda: " + raw);
            setScannerStatus("idle");
            return;
          }
          setScannedUrl(raw);
          setIframeError(false);
          setScannerStatus("idle");
        },
        () => { /* per-frame scan errors – ignore */ }
      ).catch(async (err) => {
        // Fallback to relaxed environment constraint if "exact" fails
        try {
          await instance.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
            (decodedText) => {
              try { instance.stop().catch(() => { /* ignore */ }); } catch (_) { /* ignore */ }
              const raw = (decodedText || "").trim();
              if (raw && /^https?:\/\//i.test(raw)) {
                setScannedUrl(raw);
                setIframeError(false);
              } else if (raw) {
                toast.success("QR koda: " + raw);
              }
              setScannerStatus("idle");
            },
            () => { /* ignore */ }
          );
          setScannerStatus("running");
        } catch (err2) {
          throw err2 || err;
        }
      });
      if (scannerInstanceRef.current === instance) {
        setScannerStatus("running");
      }
    } catch (e) {
      const msg = (e && (e.message || e.toString())) || "Neznana napaka";
      const perm = /Permission|NotAllowed|denied/i.test(msg);
      const nocam = /NotFound|Requested device not found/i.test(msg);
      setScannerError(perm
        ? "Brskalnik je zavrnil dostop do kamere. Dovolite dostop v nastavitvah in poskusite znova."
        : nocam
          ? "Kamera ni najdena na tej napravi."
          : ("Napaka pri zagonu skenerja: " + msg));
      setScannerStatus("error");
    }
  };

  // Start / stop scanner when dialog opens in QR mode
  useEffect(() => {
    if (open && isQrMode) {
      startScanner();
    } else {
      stopScanner();
      setScannerStatus("idle");
    }
    return () => { stopScanner(); };
  }, [open, isQrMode]);

  const closeDialog = () => {
    setOpen(false);
    setScannedUrl(null);
    setIframeError(false);
  };

  if (!settings || !settings.enabled) return null;
  if (settings.mode !== "qr" && !settings.url) return null;

  const handleFabClick = () => {
    // Za URL način (LZS / drugo povezavo) — odpri direktno v zunanjem brskalniku, brez iframe okna.
    if (settings.mode !== "qr" && settings.url) {
      window.open(settings.url, "_blank", "noopener,noreferrer");
      return;
    }
    // QR način ostane v modalu (kamera).
    setIframeError(false);
    setScannedUrl(null);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleFabClick}
        data-testid="vezice-fab"
        aria-label={label}
        title={label}
        className="fixed z-40 bottom-6 right-6 rounded-full shadow-lg
                   bg-primary hover:bg-primary/90 text-primary-foreground
                   h-14 pl-4 pr-5 flex items-center gap-2
                   transition-transform active:scale-95"
        style={{ boxShadow: "0 10px 30px -8px rgba(0,0,0,0.35)" }}
      >
        {settings.mode === "qr" ? <QrCode className="h-5 w-5" /> : <Link2 className="h-5 w-5" />}
        <span className="text-sm font-medium">{label}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[85vh] p-0 flex flex-col"
          data-testid="vezice-dialog"
        >
          <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base flex items-center gap-2">
              {isQrMode ? <QrCode className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
              {label}
              {scannedUrl && (
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {scannedUrl}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isQrMode ? "Skenirajte QR kodo z uporabo kamere" : "Vsebina strani vezic v okvirju"}
            </DialogDescription>
            <div className="flex items-center gap-2">
              {effectiveUrl && !isQrMode && (
                <a
                  href={effectiveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  data-testid="vezice-external-link"
                >
                  <ExternalLink className="h-3 w-3" />
                  Odpri v brskalniku
                </a>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={closeDialog}
                data-testid="vezice-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 relative overflow-hidden bg-muted">
            {isQrMode ? (
              <div className="absolute inset-0 flex flex-col items-center justify-start p-4 gap-3 overflow-auto">
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  <Camera className="h-4 w-4 inline mr-1" />
                  Usmerite kamero telefona na QR kodo vezice ZGS.
                </p>
                <div
                  id={QR_ELEMENT_ID}
                  className="w-full max-w-md aspect-square bg-black rounded-lg overflow-hidden relative"
                  data-testid="vezice-qr-reader"
                />
                {scannerStatus === "starting" && (
                  <p className="text-xs text-muted-foreground" data-testid="vezice-qr-starting">
                    Zaganjam kamero…
                  </p>
                )}
                {scannerStatus === "error" && (
                  <div
                    className="text-xs text-red-600 dark:text-red-400 text-center max-w-md space-y-2"
                    data-testid="vezice-qr-error"
                  >
                    <p>{scannerError}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={startScanner}
                      data-testid="vezice-qr-retry"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Poskusi znova
                    </Button>
                  </div>
                )}
                {settings.url && (
                  <a
                    href={settings.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Odpri portal ročno
                  </a>
                )}
              </div>
            ) : iframeError || !effectiveUrl ? (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <div className="max-w-md">
                  <p className="font-medium mb-2">Stran ne dovoli vgrajevanja.</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Iz varnostnih razlogov {label} ne podpira prikaza znotraj aplikacije.
                    Odprite jo v ločenem zavihku.
                  </p>
                  {effectiveUrl && (
                    <a href={effectiveUrl} target="_blank" rel="noopener noreferrer">
                      <Button data-testid="vezice-open-fallback">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Odpri {label}
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <iframe
                key={effectiveUrl}
                title={label}
                src={effectiveUrl}
                className="w-full h-full border-0"
                sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                onError={() => setIframeError(true)}
              />
            )}
          </div>
          <DialogFooter className="px-4 py-2 border-t text-[10px] text-muted-foreground">
            Če stran ne deluje pravilno, uporabite gumb za odpiranje v brskalniku.
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VeziceFAB;
